import { DefaultPrimaryButton, InformationModal } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Divider,
  Typography,
  Button,
  FormControl,
  Select,
  Autocomplete,
  TextField,
  MenuItem,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useEffect, useMemo, useState } from "react";
import type {
  duplicatePartError,
  duplicatePartPayload,
  part,
  partSKP,
} from "@/api/supplierService";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import {
  useDuplicateParts,
  useGetPartBySupplier,
  useGetPartSKPBySupplier,
} from "@/hooks/supplierHooks";
import { isAxiosError } from "axios";
import { ErrorAlert } from "@/utils/alert";
import { useToast } from "@/hooks/useToast";
import { getAxiosErrorMessage } from "@/utils/healper";
import { useQueryHandler } from "@/hooks/useQueryHandler";

interface DuplicatePartModalProps {
  open: boolean;
  handleClose: () => void;
  selectedPart: part[];
  setSelectedPart: React.Dispatch<React.SetStateAction<part[]>>;
  supplierCode: string;
}

const DuplicatePartModal = ({
  open,
  handleClose,
  selectedPart,
  setSelectedPart,
  supplierCode,
}: DuplicatePartModalProps) => {
  const [duplicateParts, setDuplicateParts] = useState<duplicatePartPayload[]>(
    [],
  );
  const [toParts, setToParts] = useState<partSKP[]>([]);
  const [errorMessages, setErrorMessages] = useState<duplicatePartError[]>([]);

  const { data: partData } = useGetPartBySupplier(supplierCode, "v2", undefined);
  const { data: partSKPData } = useGetPartSKPBySupplier(supplierCode);

  const { mutate: duplicatePartsMutate, isPending: isDuplicatePartsPending } =
    useDuplicateParts();
  const { showToast } = useToast();

  useQueryHandler({
    isFetching: isDuplicatePartsPending,
  });

  // part_id ที่มีราคาแล้ว (part ที่สร้างไว้แต่ยังไม่มีราคายังเลือกเป็น target ได้)
  const usedPartIds = useMemo(
    () =>
      new Set(
        (partData || [])
          .filter(
            (p) =>
              p.oem_price.current_price !== 0 ||
              p.spare_price.current_price !== 0,
          )
          .map((p) => p.part_id),
      ),
    [partData],
  );

  const partDropdownOptions = useMemo(() => {
    if (!partSKPData) return [];
    // filter เฉพาะ part_number ที่ part_id ยังไม่มีราคา
    const filteredSKP = partSKPData.filter(
      (part) => !usedPartIds.has(part.part_id),
    );
    const uniquePartNos = Array.from(
      new Set(filteredSKP.map((part) => part.part_number)),
    );
    return uniquePartNos.map((partNo) => ({
      label: partNo,
      value: partNo,
    }));
  }, [partSKPData, usedPartIds]);

  useEffect(() => {
    const fromParts = selectedPart.map((partItem: part) => {
      return {
        fromPartId: partItem.part_id,
        toPartId: 0,
      };
    });
    setDuplicateParts(fromParts);
    setToParts(
      selectedPart.map(() => ({
        part_number: "",
        part_id: 0,
        part_name: "",
        ac: "",
        rt: "",
      })),
    );
    setErrorMessages([]);
  }, [selectedPart]);

  const getPartString = (part: partSKP | part) => {
    if (!part.ac) return `${part.part_number} - ${part.part_name}`;
    else if (!part.rt)
      return `${part.part_number} - ${part.part_name} - ${part.ac}`;
    else
      return `${part.part_number} - ${part.part_name} - ${part.ac} - ${part.rt}`;
  };

  const validate = () => {
    let error: boolean = false;
    // ตรวจสอบ part ที่ยังไม่ได้เลือก
    for (let i = 0; i < duplicateParts.length; i++) {
      if (duplicateParts[i].toPartId === 0) {
        setErrorMessages((prev) => {
          const newErrors = prev.filter((err) => err.partId !== 0);
          newErrors.push({
            partId: 0,
            message: "Part is required",
          });
          return newErrors;
        });
        error = true;
      }
    }

    // ตรวจสอบ part ซ้ำ
    const seen = new Map<number, number>();
    for (let i = 0; i < duplicateParts.length; i++) {
      const toPartId = duplicateParts[i].toPartId;
      if (toPartId !== 0) {
        if (seen.has(toPartId)) {
          setErrorMessages((prev) => {
            const newErrors = prev.filter(
              (err) => err.partId !== duplicateParts[i].toPartId,
            );
            newErrors.push({
              partId: duplicateParts[i].toPartId,
              message: "Part is duplicated",
            });
            return newErrors;
          });
          error = true;
        } else {
          seen.set(toPartId, i);
        }
      }
    }
    return error;
  };

  const handlerDuplicatePart = (parts: duplicatePartPayload[]) => {
    if (validate()) {
      return;
    }
    const payload = parts.filter((part) => part.toPartId !== 0);
    duplicatePartsMutate(
      {
        supplierCode: supplierCode,
        payload: payload,
      },
      {
        onError: (error) => {
          if (isAxiosError(error) && error.response?.status === 422) {
            const detail = error.response.data?.detail;
            console.log(
              "Detail from 422 error:",
              detail as duplicatePartError[],
            );
            if (Array.isArray(detail)) {
              setErrorMessages(detail as duplicatePartError[]);
              return;
            }
          }
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
        onSuccess: (response) => {
          setErrorMessages(response || []);
          showToast(`Duplicate สำเร็จ จำนวน ${payload.length} รายการ`, "success");
          handleCloseModal();
        },
      },
    );
  };

  const handleCloseModal = () => {
    setDuplicateParts([]);
    setToParts([]);
    setSelectedPart([]);
    setErrorMessages([]);
    handleClose();
  };

  const columns: GridColDef<duplicatePartPayload>[] = [
    {
      field: "fromPartId",
      headerName: "จาก Part",
      renderCell: (params) => {
        const part = partData?.find(
          (partItem) => partItem.part_id === params.row.fromPartId,
        );
        return part ? getPartString(part) : "ไม่พบ Part";
      },
      width: 250,
    },
    {
      field: "toPartId",
      headerName: "ถึง Part",
      renderCell: (params) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);

        return (
          <Box
            alignItems="center"
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
          >
            <Autocomplete
              size="small"
              fullWidth
              value={
                partDropdownOptions.find(
                  (opt) => opt.value === toParts[rowIndex]?.part_number,
                ) || null
              }
              onChange={(_event, newValue) => {
                const foundPart = newValue
                  ? partSKPData?.find(
                      (part) => part.part_number === newValue?.value,
                    )
                  : null;

                setToParts((prev) => {
                  const newParts = [...prev];
                  newParts[rowIndex] = foundPart
                    ? {
                        part_number: foundPart.part_number,
                        part_id: 0,
                        part_name: "",
                        ac: "",
                        rt: "",
                      }
                    : {
                        part_number: "",
                        part_id: 0,
                        part_name: "",
                        ac: "",
                        rt: "",
                      };
                  return newParts;
                });

                setDuplicateParts((prevDup) => {
                  const newDup = [...prevDup];
                  newDup[rowIndex] = {
                    ...newDup[rowIndex],
                    toPartId: 0,
                  };
                  return newDup;
                });

                setErrorMessages((prev) =>
                  prev.filter((err) => err.partId !== params.row.toPartId),
                );
              }}
              options={partDropdownOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      style: { fontSize: "0.875rem" },
                    },
                  }}
                  sx={{ pt: 1 }}
                />
              )}
            />
          </Box>
        );
      },
      width: 150,
    },
    {
      field: "toPart",
      headerName: "",
      width: 350,
      renderCell: (params) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <FormControl
            disabled={
              partSKPData?.length === 0 || !toParts[rowIndex]?.part_number
            }
            size="small"
            sx={{ pt: 1 }}
            fullWidth
          >
            <Select
              sx={{ fontSize: "0.875rem" }}
              value={
                toParts[rowIndex]?.part_id === 0
                  ? ""
                  : toParts[rowIndex]?.part_id
              }
              onChange={(event) => {
                const partId =
                  event.target.value === null ? 0 : Number(event.target.value);
                const selectedPart = partSKPData?.find(
                  (part) => part?.part_id === partId,
                );
                setToParts((prev) => {
                  const newParts = [...prev];
                  newParts[rowIndex] = {
                    ...newParts[rowIndex],
                    part_id: partId,
                    part_name: selectedPart?.part_name || "",
                    ac: selectedPart?.ac || "",
                    rt: selectedPart?.rt || "",
                  };
                  return newParts;
                });

                setDuplicateParts((prev) => {
                  const newDup = [...prev];
                  newDup[rowIndex] = {
                    ...newDup[rowIndex],
                    toPartId: partId,
                  };
                  return newDup;
                });

                setErrorMessages((prev) =>
                  prev.filter((err) => err.partId !== params.row.toPartId),
                );
              }}
            >
              <MenuItem value={0}></MenuItem>
              {partSKPData
                ?.filter((part) => {
                  // filter เฉพาะ part_number ที่เลือก และยังไม่มีราคา
                  return toParts[rowIndex]?.part_number
                    ? part.part_number === toParts[rowIndex]?.part_number &&
                        !usedPartIds.has(part.part_id)
                    : false;
                })
                ?.map((part) => (
                  <MenuItem key={part.part_id} value={part.part_id}>
                    {getPartString(part)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "error",
      headerName: "หมายเหตุ",
      minWidth: 150,

      flex: 1,
      renderCell: (params) => {
        console.log("Rendering error cell for row:", params.row, errorMessages);
        const errorMessage = errorMessages.find(
          (error) => error.partId === params.row.toPartId,
        )?.message;
        return (
          <Box alignItems="center" width="100%" height="100%" display="flex">
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...InformationModal,
          maxHeight: "90vh",
          width: "auto",
          maxWidth: "90vw",
        }}
      >
        <CloseButton onClick={handleCloseModal} />
        <Typography variant="h2">Duplicate Part</Typography>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ width: "100%", mt: 2, p: 1 }}>
          <DataGrid
            rows={duplicateParts}
            columns={columns}
            getRowId={(row) => row.fromPartId}
            disableRowSelectionOnClick
            disableColumnSelector
            disableColumnFilter
            disableColumnMenu
            disableColumnSorting
            hideFooter
            pageSizeOptions={[]}
            sx={{
              maxHeight: "60vh",
              width: "100%",
              overflow: "auto",
            }}
          />
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            disabled={
              duplicateParts.some((part) => part.toPartId === 0) ||
              isDuplicatePartsPending ||
              errorMessages.length > 0
            }
            onClick={() => {
              handlerDuplicatePart(duplicateParts);
            }}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DuplicatePartModal;
